/**
 * Help assistant bubble (D10-UI-001, #3604).
 *
 * A floating bottom-right button on every signed-in staff screen -- mounted in
 * CrmLayout and AdminLayout only, so it never appears on public or portal
 * pages. It asks `GET /api/v1/support/status/` before drawing anything and is
 * absent from the DOM while the service is disabled (the feature is default
 * off). It steps aside while a first-visit SectionNudge or a guided tour owns
 * the corner, so the two never overlap. The assistant's name comes from the
 * status payload -- never a literal in the code (white-label guard).
 *
 * Beside it, an occasional help nudge (D10-UI-008, #4601) -- "Need any help?"
 * and what the assistant can do for a new user, a role-specific tip for one who
 * has used it; this component owns its suppression rules.
 */
import { useEffect, useRef } from "react";
import { MessageCircle } from "lucide-react";
import { useAuth, useEffectiveAuth } from "../../contexts/auth";
import { useSupportChat } from "../../contexts/supportChat";
import { useTour } from "../../contexts/tour";
import { useIsViewingAs } from "../../contexts/viewAs";
import { useSectionNudgeVisible } from "../../lib/tours/nudgePresence";
import { markHelpChatUsed } from "../../lib/support/helpNudge";
import { SupportPanel } from "./SupportPanel";
import { SupportHelpNudge } from "./SupportHelpNudge";

/** Neutral fallback if a status payload ever arrives without a name. */
const FALLBACK_NAME = "Help";

export function SupportBubble() {
  const { status, ensureStatus, isOpen, setOpen, openWithPrefill, messages } = useSupportChat();
  const { user } = useAuth();
  const { previewing } = useEffectiveAuth();
  const viewingAs = useIsViewingAs();
  const { isRunning: tourRunning } = useTour();
  const nudgeVisible = useSectionNudgeVisible();
  const userId = user?.id ?? null;
  const bubbleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!status) ensureStatus();
  }, [status, ensureStatus]);

  // Opening the chat by any route (bubble, nudge, an in-app link) counts as
  // using it: no nudge for the rest of this session, and later nudges switch
  // from "Need any help?" lines to role-specific tips (#4601).
  useEffect(() => {
    if (isOpen && userId !== null) markHelpChatUsed(userId);
  }, [isOpen, userId]);

  if (!status?.enabled) return null;
  const name = status.assistant_name?.trim() || FALLBACK_NAME;
  // Stays mounted while the panel is open (the panel covers it) so Esc can
  // return focus to it.
  const showBubble = !tourRunning && !nudgeVisible;
  // The help nudge never shows while the panel is open or a conversation is
  // in this tab, never over a tour, section nudge, welcome prompt or
  // view-as/preview, and not in offline mode (it promises an AI assistant).
  const nudgeActive =
    showBubble &&
    !isOpen &&
    messages.length === 0 &&
    !viewingAs &&
    !previewing &&
    !user?.welcome_prompt_eligible &&
    status.mode !== "offline";

  return (
    <>
      {showBubble && (
        <button
          ref={bubbleRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Open help chat with ${name}`}
          title={`Ask ${name}`}
          data-tour="support-bubble"
          className="crm-overlay-enter fixed bottom-4 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-accent)] text-white shadow-lg hover:bg-[var(--color-accent-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] transition-colors sm:bottom-6 sm:right-6"
        >
          <MessageCircle className="h-6 w-6" aria-hidden="true" />
        </button>
      )}
      {showBubble && userId !== null && (
        <SupportHelpNudge
          assistantName={name}
          userId={userId}
          active={nudgeActive}
          returnFocusRef={bubbleRef}
          usedOnServer={status.has_used_chat === true}
          onOpen={(content) =>
            content.question ? openWithPrefill(content.question) : setOpen(true)
          }
        />
      )}
      <SupportPanel assistantName={name} />
    </>
  );
}
